/**
 * usePreferences hook - manages user preferences with cookie storage
 *
 * Diamond Architecture: Client-side state management with persistence
 */
import {
  Currency,
  currencyToWire,
  DEFAULT_PREFERENCES,
  UnitSystem,
  unitSystemToWire,
  type UserPreferences,
} from '@/shared/types/preferences';
import { wireToCanonical } from '@letsgethome/lib/proto-enums/wire_aliases_gen';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Raw cookie shape: persists Currency/UnitSystem as their lowercase
 * wire strings for forward/backward compatibility with cookies
 * written before the proto-enum swap.
 */
interface PreferencesCookie {
  autoTranslate?: UserPreferences['autoTranslate'];
  locale?: UserPreferences['locale'];
  currency?: unknown;
  unitSystem?: unknown;
}

function decodeCookiePayload(raw: PreferencesCookie): Partial<UserPreferences> {
  const out: Partial<UserPreferences> = {};
  if (typeof raw.autoTranslate === 'boolean') out.autoTranslate = raw.autoTranslate;
  if (typeof raw.locale === 'string') out.locale = raw.locale;
  if (typeof raw.currency === 'string') {
    try {
      const canonical = wireToCanonical('common.v1.Currency', raw.currency);
      const c = Currency[canonical as keyof typeof Currency] as Currency | undefined;
      if (c !== undefined) out.currency = c;
    } catch {
      // Unknown / malformed cookie value: fall back to default.
    }
  }
  if (typeof raw.unitSystem === 'string') {
    try {
      const canonical = wireToCanonical('common.v1.UnitSystem', raw.unitSystem);
      const u = UnitSystem[canonical as keyof typeof UnitSystem] as UnitSystem | undefined;
      if (u !== undefined) out.unitSystem = u;
    } catch {
      // Unknown / malformed cookie value: fall back to default.
    }
  }
  return out;
}

function encodeCookiePayload(p: UserPreferences): PreferencesCookie {
  return {
    autoTranslate: p.autoTranslate,
    locale: p.locale,
    currency: currencyToWire(p.currency),
    unitSystem: unitSystemToWire(p.unitSystem),
  };
}

const PREFERENCES_COOKIE_NAME = 'user_preferences';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

function getPreferencesFromCookies(): Partial<UserPreferences> {
  if (typeof document === 'undefined') return {};

  const cookies = document.cookie.split(';');
  const prefCookie = cookies.find((c) => c.trim().startsWith(`${PREFERENCES_COOKIE_NAME}=`));

  if (!prefCookie) return {};

  try {
    const value = prefCookie.split('=')[1];
    if (value === undefined) return {};
    const raw = JSON.parse(decodeURIComponent(value)) as PreferencesCookie;
    return decodeCookiePayload(raw);
  } catch {
    return {};
  }
}

function savePreferencesToCookies(preferences: UserPreferences): void {
  if (typeof document === 'undefined') return;

  const value = encodeURIComponent(JSON.stringify(encodeCookiePayload(preferences)));
  document.cookie = `${PREFERENCES_COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

// Store for managing preferences state
let preferences: UserPreferences = DEFAULT_PREFERENCES;
const listeners: Set<() => void> = new Set();

function getSnapshot(): UserPreferences {
  return preferences;
}

function getServerSnapshot(): UserPreferences {
  return DEFAULT_PREFERENCES;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function updatePreferencesStore(updates: Partial<UserPreferences>): void {
  preferences = { ...preferences, ...updates };
  savePreferencesToCookies(preferences);
  listeners.forEach((listener) => listener());
}

// Initialize from cookies on first client load
if (typeof window !== 'undefined') {
  const stored = getPreferencesFromCookies();
  preferences = { ...DEFAULT_PREFERENCES, ...stored };
}

export function usePreferences() {
  const currentPreferences = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const updatePreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      updatePreferencesStore({ [key]: value });
    },
    []
  );

  const setAutoTranslate = useCallback(
    (value: boolean) => {
      updatePreference('autoTranslate', value);
    },
    [updatePreference]
  );

  const setCurrency = useCallback(
    (value: Currency) => {
      updatePreference('currency', value);
    },
    [updatePreference]
  );

  const setUnitSystem = useCallback(
    (value: UnitSystem) => {
      updatePreference('unitSystem', value);
    },
    [updatePreference]
  );

  return {
    preferences: currentPreferences,
    isLoaded: true,
    updatePreference,
    setAutoTranslate,
    setCurrency,
    setUnitSystem,
  };
}
